/**
 * A customer's subscription, as the renewal engine runs it: licences held
 * and in use, how they pay (each period or a year up front), what's paid,
 * the next renewal and its countdown, and whether it's set to stop. Staff
 * change the plan or stop renewing on the customer's request; both are in
 * the activity log.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Badge, Button, formatCurrency } from '@lipaflow/ui'
import { apiErrorMessage } from '@lipaflow/shared'
import { adminService } from '../../services/admin'

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })

export function SubscriptionSection({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient()
  const [confirmingStop, setConfirmingStop] = useState(false)
  const sub = useQuery({ queryKey: ['customer-subscription', tenantId], queryFn: () => adminService.getSubscription(tenantId) })
  const done = (message: string) => {
    toast.success(message)
    setConfirmingStop(false)
    queryClient.invalidateQueries({ queryKey: ['customer-subscription', tenantId] })
  }
  const plan = useMutation({
    mutationFn: (to: string) => adminService.setSubscriptionPlan(tenantId, to),
    onSuccess: (s) => done(s.plan === 'yearly' ? 'They pay a year up front from the next renewal' : `They pay each ${s.period} from the next renewal`),
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't change the plan")),
  })
  const cancel = useMutation({
    mutationFn: () => adminService.cancelSubscription(tenantId),
    onSuccess: () => done('Renewals stopped: it ends with what they have paid for'),
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't stop renewing")),
  })
  const resume = useMutation({
    mutationFn: () => adminService.resumeSubscription(tenantId),
    onSuccess: () => done('Renewals carry on'),
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't carry on renewing")),
  })

  if (sub.isLoading) return <p role="status" className="text-body-sm text-ink-muted">Loading the subscription…</p>
  if (sub.isError || !sub.data) {
    return <p role="alert" className="text-body-sm text-warning">{apiErrorMessage(sub.error, "Couldn't load the subscription.")}</p>
  }
  const s = sub.data
  const busy = plan.isPending || cancel.isPending || resume.isPending
  const noun = s.period
  const money = (n: number) => formatCurrency(n, true)

  return (
    <section aria-labelledby="customer-subscription" className="rounded-card border border-hairline p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 id="customer-subscription" className="font-inter text-caption text-ink-muted">Subscription</h4>
        <span className="flex gap-1">
          {s.is_trial && <Badge variant="info" size="sm">Trial</Badge>}
          {s.cancelling && <Badge variant="warning" size="sm">Stops at period end</Badge>}
          {!s.is_trial && !s.cancelling && <Badge variant="neutral" size="sm">{s.plan === 'yearly' ? 'Pays a year up front' : `Pays each ${noun}`}</Badge>}
        </span>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-body-sm">
        <div>
          <dt className="text-caption text-ink-faint">Licences</dt>
          <dd className="text-ink">
            {s.licences_held === null ? `${s.licences_in_use} in use` : `${s.licences_in_use} of ${s.licences_held} in use`}
          </dd>
          {s.licences_held !== null && s.licences_in_use > s.licences_held && (
            <dd className="text-caption text-ink-muted">
              {s.licences_in_use - s.licences_held} more than paid for:{' '}
              {s.current_cycle === 'yearly'
                ? 'billed as an addition from the day they were added to the end of the year'
                : 'billed with the next renewal'}
            </dd>
          )}
        </div>
        <div>
          <dt className="text-caption text-ink-faint">{s.is_trial ? 'Trial ends' : 'Paid until'}</dt>
          <dd className="text-ink">
            {s.is_trial ? (s.trial_ends_at ? day(s.trial_ends_at) : 'Starts at go-live') : s.paid_until ? day(s.paid_until) : 'Not paid yet'}
          </dd>
        </div>
        <div>
          <dt className="text-caption text-ink-faint">{s.cancelling ? 'Ends' : 'Next renewal'}</dt>
          <dd className="text-ink">
            {s.cancelling
              ? (s.ends_at ? `${day(s.ends_at)} (${s.days_left ?? 0} days)` : '—')
              : s.invoice
                ? `${s.invoice.number}, ${money(s.invoice.remaining)} ${s.invoice.status === 'open' ? 'unpaid' : s.invoice.status}`
                : s.renews_at
                  ? `Raised ${day(s.renews_at)}, ${s.notice_days} days ahead`
                  : '—'}
          </dd>
        </div>
      </dl>

      {!s.is_trial && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {s.plans.length > 1 && !s.cancelling && (
            <label className="flex items-center gap-2 text-body-sm text-ink">
              <span className="text-caption text-ink-muted">Pays</span>
              <select
                aria-label="How they pay"
                value={s.plan}
                disabled={busy}
                onChange={(e) => plan.mutate(e.target.value)}
                className="rounded-button border border-hairline bg-surface px-2 py-1 text-body-sm"
              >
                {s.plans.map((p) => (
                  <option key={p} value={p}>{p === 'yearly' ? 'A year up front' : `Each ${noun}`}</option>
                ))}
              </select>
            </label>
          )}
          {s.cancelling ? (
            <Button size="sm" variant="secondary" onClick={() => resume.mutate()} isLoading={resume.isPending}>Carry on renewing</Button>
          ) : confirmingStop ? (
            <>
              <span className="text-body-sm text-ink">Stop renewing? They keep what they've paid for{s.invoice ? `; ${s.invoice.number} is withdrawn` : ''}.</span>
              <Button size="sm" variant="secondary" onClick={() => cancel.mutate()} isLoading={cancel.isPending}>Yes, stop</Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmingStop(false)}>No</Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setConfirmingStop(true)} disabled={busy}>Stop renewing</Button>
          )}
        </div>
      )}
    </section>
  )
}
